// The agents' global surface
// (docs/Y2026-Q4/design-review-mode.md section 2.3):
//
//   L1   [Project agents]
//   L2   [Conversations] [Portfolio status] [Agent prompts] [Settings] | [Debug: Scheduled]
//        [Debug: Agent state]
//   L3   (portfolio status) one tab per specialist that keeps a status row
//        (agent prompts / agent state) one tab per agent
//
// Generated from the registry, so a new specialist adds its tabs without a change
// here. The debug tabs come last, set apart: they show how the agents work, not what
// an operator acts on, which now lives on each project's Status pages.
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";

import type {
  AgentPromptSummary,
  SharedPromptDocument,
} from "../api/hooks/useStatefulAgents";
import { useQuestionsWaiting, useStatefulAgents } from "../api/hooks/useStatefulAgents";
import { CountBadge } from "../components/CountBadge";
import { PageHeader } from "../components/PageHeader";
import { useOtherSlotsInUse } from "../api/hooks/useAgentConfigs";
import { DEFAULT_ONLY_NOTE } from "../utils/agentSlotText";
import { TabRows } from "../components/TabRows";
import { useDocumentTitle } from "../utils/useDocumentTitle";
import {
  agentPagePath,
  movedProjectAgentPath,
  outputsPath,
  PROJECT_AGENTS,
  runPath,
  teamSettingsPath,
} from "./learningAgentPaths";
import styles from "./StatefulAgentsRoute.module.css";

interface Tab {
  to: string;
  label: string;
  // The first tab of a named group carries the group's caption.
  group?: string;
}

interface Section {
  id: string;
  label: string;
  // Landing tab first. A single-tab section renders no third row.
  tabs: readonly (Tab & { badge?: number })[];
  badge: number;
  debug?: boolean;
}

// The project team's sections. Only the behavior questions each prompt page shows waiting
// on an operator are counted: decisions and clarifications are settled on each project.
const teamSections = (
  projectAgents: AgentPromptSummary[],
  documents: SharedPromptDocument[],
  questions: Record<string, number>,
): Section[] => {
  // One per specialist that keeps a status row: any other would render permanently empty,
  // which reads as broken rather than absent.
  const portfolioTabs = projectAgents
    .filter((a) => !a.is_manager && a.keeps_status_row)
    .map((a) => ({ to: outputsPath(a.agent, "portfolio"), label: a.label }));
  const portfolio: Section[] =
    portfolioTabs.length > 0
      ? [{ id: "portfolio", label: "Portfolio status", tabs: portfolioTabs, badge: 0 }]
      : [];
  // The shared documents first: they are the team's common vocabulary.
  const promptTab =
    (group: string) =>
    (a: { agent: string; label: string }, index: number) => ({
      to: agentPagePath(a.agent, "prompt"),
      label: a.label,
      badge: questions[a.agent] ?? 0,
      ...(index === 0 ? { group } : {}),
    });
  const promptTabs = [
    ...documents.map(promptTab("Shared prompts:")),
    ...projectAgents.map(promptTab("Per-specialist prompts:")),
  ];
  return [
    {
      id: "conversations",
      label: "Conversations",
      tabs: [{ to: runPath("conversations"), label: "Conversations" }],
      badge: 0,
    },
    ...portfolio,
    {
      id: "prompts",
      label: "Agent prompts",
      tabs: promptTabs,
      badge: promptTabs.reduce((sum, t) => sum + t.badge, 0),
    },
    {
      id: "settings",
      label: "Settings",
      tabs: [{ to: teamSettingsPath, label: "Settings" }],
      badge: 0,
    },
    {
      id: "scheduled",
      label: "Debug: Scheduled",
      tabs: [{ to: runPath("scheduled"), label: "Scheduled" }],
      badge: 0,
      debug: true,
    },
    {
      id: "state",
      label: "Debug: Agent state",
      tabs: projectAgents.map((a) => ({ to: agentPagePath(a.agent, "state"), label: a.label })),
      badge: 0,
      debug: true,
    },
  ];
};

// The section and tab the URL is on: the longest tab path the URL sits under, so
// a run's page stays under Scheduled however many segments it grows.
const resolve = (pathname: string, sections: readonly Section[]) => {
  let best: { section: Section; tab: Tab } | null = null;
  for (const section of sections) {
    for (const tab of section.tabs) {
      const under = pathname === tab.to || pathname.startsWith(`${tab.to}/`);
      if (under && (!best || tab.to.length > best.tab.to.length)) best = { section, tab };
    }
  }
  return best;
};

export const StatefulAgentsLayout = () => {
  const { agent } = useParams();
  const { pathname } = useLocation();
  const otherSlots = useOtherSlotsInUse();
  const agentsQuery = useStatefulAgents();
  const agents = agentsQuery.data?.agents ?? [];
  // One read for every badge on the page.
  const { byAgent: questions, total: teamWaiting } = useQuestionsWaiting();
  const inTeam = pathname.startsWith(PROJECT_AGENTS);
  const current = inTeam ? undefined : agents.find((a) => a.agent === agent);

  const team = teamSections(agents, agentsQuery.data?.documents ?? [], questions);
  const teamLanding = team[0]?.tabs[0]?.to ?? runPath("conversations");
  const resolved = inTeam ? resolve(pathname, team) : null;
  // The tab path the URL is on, so an open tab names its own page rather than the
  // whole surface: "Agents > Agent prompts > Grid". A section
  // whose only tab repeats its name (Scheduled, Conversations) is not said twice.
  // Resolved up here because the hook below cannot sit after an early return.
  const trail = resolved
    ? [resolved.section.label, resolved.tab.label].filter(
        (label, index, all) => index === 0 || label !== all[index - 1],
      )
    : [];

  useDocumentTitle(["Agents", ...trail].join(" > "));

  if (agentsQuery.isLoading) {
    return <p className={styles.muted}>Loading agents…</p>;
  }
  if (agentsQuery.error) {
    return <p className={styles.error}>Could not load the agents.</p>;
  }

  if (!inTeam) {
    // No agent, or one the registry does not know: the team's landing tab, not an
    // empty page that looks like no data.
    if (!current) return <Navigate to={teamLanding} replace />;
    // An agent's pages moved under the team; its old links still resolve.
    const rest = pathname.split(`/agents/${agent ?? ""}`)[1] ?? "";
    return (
      <Navigate
        to={movedProjectAgentPath(current.agent, current.is_manager, rest.replace(/^\//, ""))}
        replace
      />
    );
  }
  // The team's root, or a bare section prefix, has no page of its own.
  if (!resolved) return <Navigate to={teamLanding} replace />;
  const { section, tab } = resolved;

  return (
    <>
      <PageHeader title="Agents" description={otherSlots && DEFAULT_ONLY_NOTE} />
      <TabRows
        rows={[
          {
            ariaLabel: "Agents",
            items: [
              {
                to: teamLanding,
                label: "Project agents",
                active: true,
                badge: <CountBadge count={teamWaiting} />,
              },
            ],
          },
          {
            ariaLabel: "Sections",
            items: team.map((s) => ({
              to: s.tabs[0].to,
              label: s.label,
              active: s.id === section.id,
              badge: <CountBadge count={s.badge} />,
              debug: s.debug,
            })),
          },
          {
            ariaLabel: `${section.label} sections`,
            items: section.tabs.map((t) => ({
              to: t.to,
              label: t.label,
              active: t.to === tab.to,
              badge: <CountBadge count={t.badge ?? 0} />,
              group: t.group,
            })),
          },
        ]}
      />
      <div className={styles.sectionBody}>
        <Outlet />
      </div>
    </>
  );
};
